import { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { 
  LayoutDashboard, 
  Users, 
  Megaphone, 
  Settings, 
  LogOut,
  Menu,
  User,
  AlertTriangle,
  ChevronRight
} from 'lucide-react';

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { name: 'Kişiler', href: '/contacts', icon: Users },
  { name: 'Kampanyalar', href: '/campaigns', icon: Megaphone },
  { name: 'Ayarlar', href: '/settings', icon: Settings },
];

export default function MainLayout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const NavItems = ({ mobile = false }) => (
    <nav className="space-y-1">
      {navigation.map((item) => {
        const isActive = location.pathname === item.href || 
          (item.href !== '/dashboard' && location.pathname.startsWith(item.href));
        
        return (
          <Link
            key={item.name}
            to={item.href}
            onClick={() => mobile && setSidebarOpen(false)}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
              isActive 
                ? 'bg-primary text-primary-foreground' 
                : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
            }`}
            data-testid={`nav-${item.name.toLowerCase()}`}
          >
            <item.icon className="w-5 h-5" />
            {item.name}
            {isActive && <ChevronRight className="w-4 h-4 ml-auto" />}
          </Link>
        );
      })}
    </nav>
  );

  return (
    <div className="min-h-screen bg-[#F4F4F5]">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-col flex-1 bg-white border-r border-border">
          {/* Logo */}
          <div className="flex items-center h-16 px-6 border-b border-border">
            <Link to="/dashboard" className="flex items-center gap-2">
              <h1 className="text-xl font-bold tracking-tight font-['Outfit']">SesliAsistan</h1>
            </Link>
          </div>
          
          {/* Mock Mode Badge */}
          <div className="px-4 py-3 border-b border-border">
            <Badge 
              className="w-full justify-center bg-[#F97316]/10 text-[#F97316] border-[#F97316]/20 hover:bg-[#F97316]/20"
              data-testid="mock-mode-badge"
            >
              <AlertTriangle className="w-3 h-3 mr-1" />
              MOCK MODU AKTİF
            </Badge>
          </div>
          
          {/* Navigation */}
          <div className="flex-1 px-4 py-4 overflow-y-auto">
            <NavItems />
          </div>
          
          {/* User Menu */}
          <div className="p-4 border-t border-border">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="w-full justify-start gap-3 h-auto py-2">
                  <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                    <User className="w-4 h-4 text-primary-foreground" />
                  </div>
                  <div className="flex-1 text-left min-w-0">
                    <p className="text-sm font-medium truncate">{user?.full_name}</p>
                    <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>Hesabım</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="logout-btn">
                  <LogOut className="w-4 h-4 mr-2" />
                  Çıkış Yap
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="lg:hidden">
        <div className="sticky top-0 z-40 flex items-center h-16 px-4 bg-white border-b border-border glass-header">
          <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
                <Menu className="w-5 h-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-64 p-0">
              <div className="flex flex-col h-full">
                <div className="flex items-center h-16 px-6 border-b border-border">
                  <h1 className="text-xl font-bold tracking-tight font-['Outfit']">SesliAsistan</h1>
                </div>
                
                <div className="px-4 py-3 border-b border-border">
                  <Badge className="w-full justify-center bg-[#F97316]/10 text-[#F97316] border-[#F97316]/20">
                    <AlertTriangle className="w-3 h-3 mr-1" />
                    MOCK MODU AKTİF
                  </Badge>
                </div>
                
                <div className="flex-1 px-4 py-4 overflow-y-auto">
                  <NavItems mobile />
                </div>
                
                <div className="p-4 border-t border-border">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                      <User className="w-4 h-4 text-primary-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{user?.full_name}</p>
                      <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                    </div>
                  </div>
                  <Button 
                    variant="outline" 
                    className="w-full" 
                    onClick={handleLogout}
                    data-testid="mobile-logout-btn"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Çıkış Yap
                  </Button>
                </div>
              </div>
            </SheetContent>
          </Sheet>
          
          <h1 className="ml-3 text-lg font-bold tracking-tight font-['Outfit']">SesliAsistan</h1>
          
          <Badge 
            className="ml-auto bg-[#F97316]/10 text-[#F97316] border-[#F97316]/20 text-xs"
          >
            MOCK
          </Badge>
        </div>
      </div>

      {/* Main Content */}
      <main className="lg:pl-64">
        <div className="max-w-[1600px] mx-auto p-6 md:p-8 lg:p-12">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
